"use client";

import Link from "@/app/components/LocalizedLink";
import { useTranslation } from "react-i18next";
import {
  InstagramIcon,
  LinkedInIcon,
  FacebookIconOutline as FacebookIcon,
  LogoIcon,
} from "../Icons";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="px-3 pb-3 sm:px-4 sm:pb-4">
      <div className="glass mx-auto max-w-[1360px] rounded-glassPanel px-5 pb-8 text-signal-ink sm:px-8 sm:pb-10 lg:px-12">
        <div className="h-[80px] sm:h-[100px] border-b border-signal-ink/10 flex items-center justify-between">
          <button
            className="block w-[173px]  h-[42px]"
            onClick={e => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            aria-label="Scroll to top"
          >
            <LogoIcon color="#0B1B33" />
          </button>
          <div className="flex gap-2">
            <a
              href="https://www.facebook.com/profile.php?id=61579204259799"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Facebook page"
              className="btn-glass-dark h-10 w-10"
            >
              <FacebookIcon />
            </a>

            <a
              href="https://www.instagram.com/heyfinn.ai/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our Instagram page"
              className="btn-glass-dark h-10 w-10"
            >
              <InstagramIcon />
            </a>

            <a
              href="https://www.linkedin.com/company/108146940/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our LinkedIn page"
              className="btn-glass-dark h-10 w-10"
            >
              <LinkedInIcon />
            </a>
          </div>
        </div>
        <div className="flex flex-col justify-between md:flex-row py-6 sm:py-8 gap-6 md:gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2 flex-1">
            {/* <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
              {t("footerCompanyTitle")}
            </h3> */}
            <p className="text-[14px] sm:text-[16px] max-w-2xl leading-relaxed mb-4 text-signal-muted">
              {t("footerCompanyDescription")}
            </p>
            {/* Footer Keywords - Subtle display for SEO */}
            {(() => {
              const keywords = t("footerKeywords", { returnObjects: true }) as
                | Record<string, string>
                | undefined;
              if (!keywords || typeof keywords !== "object") return null;
              return (
                <div className="mb-4 text-[12px] sm:text-[13px] text-signal-muted space-y-1">
                  <span className="inline-block mr-3">
                    {keywords.kostenlosesAts || ""}
                  </span>
                  <span className="inline-block mr-3">
                    {keywords.karriereseiteErstellen || ""}
                  </span>
                  <span className="inline-block mr-3">
                    {keywords.handwerkerSoftware || ""}
                  </span>
                  <span className="inline-block">
                    {keywords.bewerbermanagement || ""}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Quick Links */}
          <div className="flex-shrink-0">
            <h4 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4">
              {t("footerQuickLinksTitle")}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  href="/dashboard"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerDashboardLink")}
                </Link>
              </li>
              <li>
                <Link
                  href="/open-positions"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerOpenPositionsLink")}
                </Link>
              </li>
              <li>
                <Link
                  href="/home/pricing"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerPricingLink")}
                </Link>
              </li>
              <li>
                <Link
                  href="/login"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerLoginLink")}
                </Link>
              </li>
              <li>
                <Link
                  href="/register"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerRegisterLink")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="flex-shrink-0">
            <h4 className="text-lg sm:text-2xl font-semibold mb-3 sm:mb-4">
              {t("footerLegalTitle")}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  href="https://achtfach.ch/datenschutz"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerPrivacyPolicyLink")}
                </Link>
              </li>
              <li>
                <Link
                  href="/imprint"
                  className="font-medium text-[13px] sm:text-[14px] underline tracking-tight text-signal-ink hover:text-signal-deep"
                >
                  {t("footerImprintLink")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-signal-ink/10 pt-6 sm:pt-8 text-center">
          <p className="text-signal-muted text-xs sm:text-sm font-medium px-2">
            © 2026 heyfinn.ai. {t("footerAllRightsReserved")}
          </p>
        </div>
      </div>
    </footer>
  );
}
